import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const EMPTY = { name: "", description: "", display_order: 0, status: "active" };

export default function AdminCategories() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/categories").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const payload = { ...form, display_order: Number(form.display_order) || 0 };
    try {
      if (editing) await api.put(`/admin/categories/${editing.id}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { if (window.confirm("Delete category?")) { await api.delete(`/admin/categories/${id}`); load(); } };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Categories</h1>
        <Button className="bg-blue-800 hover:bg-blue-900" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="add-category-btn"><Plus className="mr-1.5 h-4 w-4" />Add Category</Button>
      </div>
      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full min-w-[500px] text-left text-sm">
          <thead className="bg-slate-50 text-slate-500"><tr><th className="p-3">Order</th><th className="p-3">Name</th><th className="p-3">Status</th><th className="p-3">Actions</th></tr></thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100">
                <td className="p-3 text-slate-400">{c.display_order}</td>
                <td className="p-3 font-medium text-slate-900">{c.name}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-xs capitalize ${c.status === "active" ? "bg-green-50 text-green-700" : "bg-slate-100 text-slate-500"}`}>{c.status}</span></td>
                <td className="p-3"><div className="flex gap-1"><Button size="icon" variant="ghost" onClick={() => { setEditing(c); setForm({ ...EMPTY, ...c }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button><Button size="icon" variant="ghost" onClick={() => del(c.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit" : "Add"} Category</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="category-name" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
            <div><Label>Display Order</Label><Input type="number" value={form.display_order} onChange={(e) => set("display_order", e.target.value)} /></div>
          </div>
          <DialogFooter><Button className="bg-blue-800 hover:bg-blue-900" onClick={save} data-testid="save-category-btn">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
